import "./PayoffDiagramComponent.scss";
import {IHttpService, IOnChanges, IOnChangesObject, IOnDestroy, IOnInit, IScope, ITimeoutService} from "angular";
import {PayoffChartData} from "../payoff-chart/types/PayoffChartData";
import {StringKeyed} from "../../../../types/GenericTypes";
import {SystemEventService} from "../../../../services/system-state/SystemEventService";
import {Optional} from "../../../../js/util/Optional";
import {PayoffService} from "../service/PayoffService";
import {PayoffDiagramTimeInputs} from "./models/PayoffDiagramTimeInputs";
import {Observable, ReplaySubject, Subject} from "rxjs";
import {ScenarioDefinitions, TfPayoffRequest} from "../service/PayoffRequests";
import {TfPayoffResponse} from "../service/PayoffResponses";
import {PayoffDiagramInputData} from "./models/PayoffDiagramInputData";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";

export const PayoffDiagram = "tfPayoffDiagram";

export function payoffDiagram() {
    return {
        templateUrl: "app/components/charts/payoff/payoff-diagram/PayoffDiagramComponent.html",
        transclude: {
            controlsSlot: "?tfPayoffDiagramTopControls",
            controlsRightSlot: "?tfPayoffDiagramControlsRight"
        },
        bindings: {
            height: "<",
            data: "<",
            priceModifier: "<",
            scenarios: "<",
            priceSteps: "<",
            pricePoints: "<",
            volUp: "<",
            volDown: "<"
        },
        controller: PayoffDiagramComponentController,
        controllerAs: "ctrl"
    };
}

export class PayoffDiagramComponentController implements IOnInit, IOnChanges, IOnDestroy {

    public height: number;
    public data: PayoffDiagramInputData;

    public priceModifier: any;
    public scenarios?: ScenarioDefinitions | null;
    public priceSteps: number;
    public pricePoints: number;
    public volUp: number;
    public volDown: number;

    private inputs: PayoffDiagramTimeInputs | null;

    public chartData: PayoffChartData;

    private seriesState: StringKeyed<boolean> = {};
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    private calculate$ = new ReplaySubject<void>(1);

    /** @ngInject */
    public constructor(private $http: IHttpService,
                       private $scope: IScope,
                       private $timeout: ITimeoutService,
                       private systemEventService: SystemEventService,
                       private payoffService: PayoffService) {
    }

    public $onInit(): void {
        this.calculate$
            .takeUntil(this.destroyed$)
            .debounceTime(200)
            .map(this.buildRequest.bind(this))
            .switchMap(this.retrievePayoffData.bind(this))
            .subscribe(this.updateChartData.bind(this));
    }

    public $onChanges(changes: IOnChangesObject) {
        if (changes.data != null) {
            const previous = changes.data.previousValue;
            const current = changes.data.currentValue;
            if (previous == null || current == null || (previous.id !== current.id)) {
                this.calculatePayoff(true);
            } else {
                this.calculatePayoff(false);
            }
        } else if (this.data != null) {
            this.calculatePayoff(false);
        }
    }

    private async calculatePayoff(resetState: boolean) {
        if (resetState) {
            this.seriesState = {};
        }
        this.calculate$.next(void 0);
    }

    public updateTimeInputs(updated: PayoffDiagramTimeInputs | null) {
        this.$timeout(() => this.inputs = updated);
        this.calculatePayoff(false);
    }

    private buildRequest(): TfPayoffRequest {
        const optionTrades = Optional.of(this.data)
                                     .map((data) => data.optionTrades)
                                     .orElse([]);
        const equityTrades = Optional.of(this.data)
                                     .map((data) => data.equityTrades)
                                     .orElse([]);

        const timeSeries = Optional.of(this.inputs)
                                   .map(i => i.timeSeries)
                                   .orNull();
        const pricePoints = Optional.of(this.inputs)
                                    .map(i => i.pricePoints)
                                    .orNull();

        const scenarios = Optional.of(this.scenarios)
                                  .map((scenarios) => ObjectUtil.pick(scenarios, "asAt", "modelType", "marketScenario", "sectorScenarios", "securityScenarios"))
                                  .orNull();
        return {
            timeSteps: timeSeries,
            priceSteps: pricePoints,
            scenarios,
            trades: optionTrades,
            equityTrades
        };
    }

    private retrievePayoffData(params: TfPayoffRequest): Observable<TfPayoffResponse> {
        const response = this.payoffService.tfCalculatePayoff(params);
        return Observable.fromPromise(response);
    }

    private updateChartData(response: TfPayoffResponse) {
        this.$timeout(() => {
            this.chartData = { payoffSummary: response, seriesState: this.seriesState };
        });
    }

    public legendClick(data: Array<NVD3ChartSeries>) {
        this.seriesState = data.map(series => ({[series.key]: series.disabled}))
                               .reduce(Object.assign, {});
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
